import { getToken, getUserId, getUserName } from "./UserService";
import AppEnv from "../utils/AppEnv";

export interface WebSocketMessage {
  type: "category.save.started" | "category.save.completed";
  catalogId: string;
  categoryId: string;
  requestId?: string;
  updatedAt: string;
  statusCode?: number;
  errorMessage?: string;
}

type MessageHandler = (message: WebSocketMessage) => void;
type OnOpenHandler = () => void;

class AsyncEventsWebSocketService {
  private socket: WebSocket | null = null;
  private messageHandler: MessageHandler | null = null;
  private onOpenHandler: OnOpenHandler | null = null;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private currentAccountId: string | null = null;
  private shouldReconnect: boolean = true;
  private isInitialConnection: boolean = true;

  setMessageHandler(handler: MessageHandler): void {
    this.messageHandler = handler;
  }

  setOnOpenHandler(handler: OnOpenHandler): void {
    this.onOpenHandler = handler;
  }

  async connect(accountId: string, isReconnect: boolean = false): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) return;

    // Clear any pending reconnection attempts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Track if this is initial connection or reconnection
    if (!isReconnect && accountId !== this.currentAccountId) {
      // New account or first connection
      this.isInitialConnection = true;
    } else if (isReconnect) {
      // This is a reconnection, don't fetch snapshot
      this.isInitialConnection = false;
    }

    this.currentAccountId = accountId;
    this.shouldReconnect = true;

    const websocketUrl = AppEnv.WEBSOCKET_ASYNC_EVENTS_URL;
    if (!websocketUrl || websocketUrl === "undefined" || websocketUrl.includes("undefined")) {
      console.error("WEBSOCKET_ASYNC_EVENTS_URL is not configured or invalid:", websocketUrl);
      return;
    }

    const userId = getUserId();
    const userName = getUserName() ?? "";
    const token = await getToken();

    if (!userId || !token || !accountId) return;

    const queryParams = new URLSearchParams({
      "x-account-id": accountId,
      "x-user-id": userId,
      "x-user-name": userName,
      token: `Bearer ${token}`,
    });

    // Ensure baseUrl has a trailing slash, then add query params
    const baseUrl = websocketUrl.endsWith("/") ? websocketUrl : `${websocketUrl}/`;
    const wsUrl = `${baseUrl}?${queryParams.toString()}`;

    this.socket = new WebSocket(wsUrl);
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log("WebSocket connected");
      // Only call onOpenHandler on initial connection, not on reconnection
      if (this.isInitialConnection) {
        this.onOpenHandler?.();
        this.isInitialConnection = false; // Mark as no longer initial
      }
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("📨 WebSocket message received");
        this.messageHandler?.(message);
      } catch (error) {
        console.error("Invalid WebSocket message:", error, event.data);
      }
    };

    this.socket.onerror = () => {
      // Transport-level only
      console.error("WebSocket transport error");
    };

    this.socket.onclose = (event) => {
      console.warn("WebSocket closed", { code: event.code, reason: event.reason });
      this.socket = null;

      // Attempt to reconnect if connection was not intentionally closed
      // Don't reconnect for normal closure or abnormal closure or if shouldReconnect is false
      if (this.shouldReconnect && event.code !== 1000 && event.code !== 1006 && this.currentAccountId) {
        this.reconnectTimeoutId = setTimeout(() => {
          if (this.shouldReconnect && this.currentAccountId) {
            console.log("Reconnecting WebSocket...");
            this.connect(this.currentAccountId, true);
          }
        }, 3000);
      }
    };
  }

  disconnect(): void {
    this.shouldReconnect = false;
    
    // Clear any pending reconnection
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    this.socket?.close();
    this.socket = null;
    this.currentAccountId = null;
    this.isInitialConnection = true;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const asyncEventsWebSocketService = new AsyncEventsWebSocketService();
